import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getDoc, doc, setDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import TaskList from './components/TaskList';
import './App.css';

// Lazy-load other components
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const ThankYouModal = lazy(() => import('./components/ThankYouModal'));
const Footer = lazy(() => import('./components/Footer'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const Help = lazy(() => import('./components/Help'));
const Headline = lazy(() => import('./components/Headline'));

const App = () => {
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        setIsPremium(userDoc.exists() && userDoc.data().isPremium);
      } else {
        setIsPremium(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <Router>
      <div className="app">
        {/* Conditionally render the Headline component */}
        {!isPremium && (
          <Suspense fallback={<div>Loading...</div>}>
            <Headline text="🔥 Flash Giveaway! 90% Off 🎉 Premium Membership – Limited Time Sale Only! 🔥" />
          </Suspense>
        )}
        <Header />
        <Suspense fallback={<div>Loading content...</div>}>
          <AppContent isPremium={isPremium} setIsPremium={setIsPremium} />
        </Suspense>
        <Suspense fallback={<div>Loading footer...</div>}>
          <Footer />
        </Suspense>
      </div>
    </Router>
  );
};

const AppContent = ({ isPremium, setIsPremium }) => {
  const [language, setLanguage] = useState('JavaScript');
  const [difficulty, setDifficulty] = useState('All');
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const noSidebarPaths = ['/checkout', '/contact-us', '/help', '/upcoming'];
  const handleNavigateToCheckout = () => {
    navigate('/checkout');
  };

  const handleBuyPremium = async () => {
    const user = auth.currentUser;
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, { isPremium: true }, { merge: true });
      setIsPremium(true);
      navigate('/'); // Redirect to homepage
    }
  };

  useEffect(() => {
    if (isPremium && location.pathname === '/') {
      setShowThankYouModal(true);
    }
  }, [isPremium, location]);

  useEffect(() => {
    if (isPremium && location.pathname === '/checkout') {
      navigate('/');
    }
  }, [isPremium, location, navigate]);

  return (
    <div className="app-content">
      {!noSidebarPaths.includes(location.pathname) && (
        <Sidebar
          language={language}
          setLanguage={setLanguage}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
          isPremium={isPremium}
          handleNavigateToCheckout={handleNavigateToCheckout}
        />
      )}

      <Suspense fallback={<div>Loading routes...</div>}>
        <Routes>
          <Route
            path="/"
            element={
              <TaskList
                language={language}
                difficulty={difficulty}
                isPremium={isPremium}
                handleNavigateToCheckout={handleNavigateToCheckout}
              />
            }
          />
          {!isPremium && (
            <Route
              path="/checkout"
              element={<CheckoutPage onTransactionComplete={handleBuyPremium} />}
            />
          )}
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/help" element={<Help />} />
        </Routes>
      </Suspense>

      <Suspense fallback={<div>Loading modal...</div>}>
        {showThankYouModal && (
          <ThankYouModal
            isOpen={showThankYouModal}
            onClose={() => setShowThankYouModal(false)}
          />
        )}
      </Suspense>
    </div>
  );
};

export default App;
